/**
 * GEMAS: piedras preciosas que dropean AL MINAR, con probabilidad y rareza escaladas por
 * el tier del mineral (tier alto → más chance y gemas más raras). Catálogo data-driven:
 * agregar una gema = una entrada en `GEMS`. Su UTILIDAD (encantar/craftear/vender) se
 * define más adelante; por ahora dropean, se apilan y se venden.
 *
 * Ver docs/MINING_AND_SMITHING.md §Gemas.
 */

export interface GemDef {
  /** ID del item (inglés). */
  id: string;
  /** Nombre visible (español). */
  name: string;
  /** Ícono en client/assets/item/<icon>.png. */
  icon: string;
  /** Rango de rareza 1 (común) … 4 (muy rara). */
  rank: number;
  /**
   * Chance (0..1) de que —SI el drop de gema fue exitoso— la gema sea ÉSTA (2ª tirada).
   * Suma 1 entre todas. Las 2 más raras son MUY bajas.
   */
  dropChance: number;
  /** Precio de COMPRA (la venta es el 30%). Escala de 1000 en adelante. */
  buy: number;
}

/** Catálogo de gemas, de común a muy rara (dropChance suma 1). */
export const GEMS: GemDef[] = [
  { id: 'gem_aquamarine', name: 'Aguamarina', icon: 'gem_aquamarine', rank: 1, dropChance: 0.6, buy: 1000 },
  { id: 'gem_amber', name: 'Ámbar', icon: 'gem_amber', rank: 2, dropChance: 0.32, buy: 2500 },
  { id: 'gem_turquoise', name: 'Turquesa', icon: 'gem_turquoise', rank: 3, dropChance: 0.06, buy: 7000 },
  { id: 'gem_ruby', name: 'Rubí', icon: 'gem_ruby', rank: 4, dropChance: 0.02, buy: 18000 },
];

/** Chance de que un pico saque una gema, por índice de tier del mineral (0=hierro). */
const GEM_BASE_CHANCE = 0.006; // hierro (primer tier)
const GEM_CHANCE_PER_TIER = 0.005; // +0.5% por tier → obsidiana ~3.1% (idx 5)
const MAX_TIER_IDX = 5; // índice del último tier (TOOL_TIERS.length-1: iron..obsidian, 6 tiers)

/** Chance (0..1) de dropear UNA gema al minar un nodo de índice de tier `tierIdx` (1ª tirada). */
export function gemDropChance(tierIdx: number): number {
  const t = Math.max(0, Math.min(MAX_TIER_IDX, tierIdx));
  return GEM_BASE_CHANCE + t * GEM_CHANCE_PER_TIER;
}

/**
 * Rollea un drop de gema en 2 etapas: (1) ¿dropea gema? (chance por tier del mineral +
 * `chanceBonus` aditivo del player, ej. mod `miningGemChanceBonus`); (2) si sí, ¿cuál? por
 * `dropChance` fija (las 2 raras muy bajas). Devuelve el itemId o null.
 */
export function rollGemDrop(
  tierIdx: number,
  rand: () => number = Math.random,
  chanceBonus = 0
): string | null {
  const chance = Math.min(1, gemDropChance(tierIdx) + Math.max(0, chanceBonus));
  if (rand() >= chance) return null; // 1ª tirada (chance base por tier + bonus del player)
  const total = GEMS.reduce((a, g) => a + g.dropChance, 0); // 2ª tirada (ponderada)
  let roll = rand() * total;
  for (const gem of GEMS) {
    roll -= gem.dropChance;
    if (roll <= 0) return gem.id;
  }
  return GEMS[GEMS.length - 1].id;
}
